import PrimaryInnerGlowButton from '@/components/buttons/primary';
import SecondaryDefaultButton from '@/components/buttons/secondary';
import Image from '@/components/Image';
import { Container } from '@/components/layout/Container';
import Link from '@/components/Link';
import { PRIMARY_NAV_HEIGHT } from '@/components/navigation/PrimaryNavigation';
import { CONTACT_US, HOMEPAGE } from '@/constants/routes';
import gtm from '@/lib/gtm';
import { mediaBreakpointUp, paddingY } from '@/styles/layout/utils';
import { FONT_WEIGHTS } from '@/styles/typography';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import { FC } from 'react';
import styled from 'styled-components';

const PageWrapper = styled.div`
  margin-top: ${remCalc(PRIMARY_NAV_HEIGHT)};
  ${paddingY(16)}

  ${mediaBreakpointUp('tablet')} {
    ${paddingY(40)}
  }

  ${mediaBreakpointUp('desktop')} {
    ${paddingY(100)}
  }

  h1 {
    ${customTextSize(40, 42)}
    margin-bottom: ${remCalc(16)};

    ${mediaBreakpointUp('tablet')} {
      font-weight: ${FONT_WEIGHTS.SEMIBOLD};
      text-align: center;
    }

    ${mediaBreakpointUp('desktop')} {
      text-align: left;
    }
  }

  p {
    ${customTextSize(18, 29)}

    ${mediaBreakpointUp('tablet')} {
      text-align: center;
    }

    ${mediaBreakpointUp('desktop')} {
      text-align: left;
    }
  }
`;

const StyledContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  grid-gap: ${remCalc(40)};

  ${mediaBreakpointUp('desktop')} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${remCalc(16)};
  justify-content: flex-start;

  margin-top: ${remCalc(32)};

  ${mediaBreakpointUp('tablet')} {
    justify-content: center;
  }

  ${mediaBreakpointUp('desktop')} {
    justify-content: flex-start;
  }
`;

const ContentContainer = styled.div`
  ${mediaBreakpointUp('desktop')} {
    flex: 1 0 ${remCalc(400)};
  }
`;

const ImageContainer = styled.div`
  display: flex;
  justify-content: center;

  ${mediaBreakpointUp('desktop')} {
    flex: 1 1 ${remCalc(500)};
    margin-left: ${remCalc(40)};
  }
`;

interface EmailOptInOutLayoutProps {
  heading: string;
  text?: string;
  analyticsCategory: string;
  image?: {
    url: string;
    width: number;
    height: number;
    altText: string;
  };
}

const EmailOptInOutLayout: FC<EmailOptInOutLayoutProps> = (props) => {
  return (
    <PageWrapper>
      <StyledContainer>
        <ContentContainer>
          <h1>{props.heading ?? ''}</h1>
          <p>{props.text ?? ''}</p>

          <ButtonContainer>
            <Link href={HOMEPAGE}>
              <PrimaryInnerGlowButton
                as="a"
                onClick={() => {
                  gtm.track('click', {
                    element_type: 'link',
                    element_label: 'Back to homepage',
                    location: props.analyticsCategory,
                  });
                }}
              >
                Back to homepage
              </PrimaryInnerGlowButton>
            </Link>

            <SecondaryDefaultButton
              as="a"
              href={CONTACT_US}
              onClick={() => {
                gtm.track('click', {
                  element_type: 'link',
                  element_label: 'Contact Us',
                  location: props.analyticsCategory,
                });
              }}
            >
              Contact us
            </SecondaryDefaultButton>
          </ButtonContainer>
        </ContentContainer>

        <ImageContainer>
          {props.image && (
            <Image
              src={props.image.url}
              width={props.image.width}
              height={props.image.height}
              alt={props.image.altText}
            />
          )}
        </ImageContainer>
      </StyledContainer>
    </PageWrapper>
  );
};

export default EmailOptInOutLayout;
